// Components
import { DefaultButton, Persona, PersonaSize, Spinner, SpinnerSize, useTheme } from "@fluentui/react";
import { UserContainer } from "../user/UserContainer";
// Models
import { ContentType } from "@ms/uno-models/lib/local/service/graph/general/ContentType";
// Styles
import { getClassNames, getStyles } from "./CommentCard.styles";
import * as React from "react";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { sanitizeContent } from "@ms/uno-utilities/lib/local/ContentSanitizationUtilities";
import { createTrustedHtml } from "@ms/uno-utilities/lib/local/TrustedTypeUtilities";
/**
 * Card for rendering a comment in a card format
 */ export function CommentCard({ comment, commenter, commentType, commentId, commenterPhoto, onRenderPersona, styles, commentTime, onReloadCommentClicked }) {
    const theme = useTheme();
    const classNames = getClassNames(getStyles(theme, styles));
    const formattedDisplayName = commenter?.formattedDisplayName ?? Strings.TaskComments_UnknownUserLabel;
    const sanitizedComment = React.useMemo(()=>{
        return comment !== null && commentType === ContentType.HTML ? sanitizeContent(comment) : comment;
    }, [
        comment,
        commentType
    ]);
    /**
     * Render the commenter's persona
     * @param formattedDisplayName Display name of the commenter
     */ function renderCommenterPersona(formattedDisplayName) {
        const personaProps = {
            text: formattedDisplayName,
            imageUrl: commenterPhoto ?? undefined,
            imageShouldFadeIn: true,
            hidePersonaDetails: true,
            size: PersonaSize.extraSmall
        };
        return /*#__PURE__*/ React.createElement(UserContainer, {
            user: commenter,
            onLoad: onRenderPersona,
            containerId: `userContainer-commentCard-${commentId}-${commenter?.id}`
        }, /*#__PURE__*/ React.createElement(Persona, personaProps));
    }
    /**
     * Render the comment's Content
     * @param content Content to render
     * @param contentType Markup type of the comment (Text/HTML)
     * @param classNames ICommentCardClassNames: Classnames associated with the computed styles
     */ function renderCommentContent(classNames, content, contentType) {
        if (content !== null && contentType !== null) {
            switch(contentType){
                case ContentType.Text:
                    return /*#__PURE__*/ React.createElement("div", {
                        className: classNames.content
                    }, content);
                case ContentType.HTML:
                    /* eslint-disable react/no-danger */ return /*#__PURE__*/ React.createElement("div", {
                        className: classNames.content
                    }, /*#__PURE__*/ React.createElement("div", {
                        className: "htmlContent",
                        dangerouslySetInnerHTML: {
                            __html: createTrustedHtml(content, true)
                        }
                    }));
                /* eslint-enable react/no-danger */ default:
                    return null;
            }
        }
        return /*#__PURE__*/ React.createElement(DefaultButton, {
            className: classNames.reloadCommentButton,
            onClick: onReloadCommentButtonClicked
        }, Strings.CommentThreadView_CommentCardReloadCommentButtonText);
    }
    function onReloadCommentButtonClicked(evt) {
        onReloadCommentClicked(commentId);
    }
    /**
     * Render a timestamp or spinner based on the comment state
     * @param classNames ICommentCardClassNames: Classnames associated with the computed styles
     */ function renderTimeStampOrSpinner(classNames) {
        if (commentTime != null) {
            return /*#__PURE__*/ React.createElement("div", {
                className: classNames.timestamp
            }, commentTime.format("LLL"));
        } else {
            return /*#__PURE__*/ React.createElement(Spinner, {
                size: SpinnerSize.xSmall
            });
        }
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root,
        "data-is-focusable": true
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.body
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.header
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.commenterIcon,
        tabIndex: -1
    }, renderCommenterPersona(formattedDisplayName)), /*#__PURE__*/ React.createElement("div", {
        className: classNames.commenterName
    }, formattedDisplayName), renderTimeStampOrSpinner(classNames)), renderCommentContent(classNames, sanitizedComment, commentType)));
}
